/**
 * /onboarding — 4-step calm flow shown ONCE after a user verifies their email.
 *
 *   Step 1: How would you like Jesus to address you? (display_name)
 *   Step 2: Response style — Gentle / Direct / Scholarly
 *   Step 3: Denomination (optional) — Catholic / Protestant / Orthodox / Other / Skip
 *   Step 4: Memory + disclosure — required ack + optional memory consent
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getMe, postOnboarding } from "@/lib/api";

const STYLES = [
  { id: "gentle",    title: "Gentle",    body: "Warm, reflective, and patient. Lets silence speak." },
  { id: "direct",    title: "Direct",    body: "Clear, plain-spoken. Concrete next steps." },
  { id: "scholarly", title: "Scholarly", body: "Cites scripture and historical context more often." },
];

const DENOMS = [
  { id: "catholic",    title: "Catholic" },
  { id: "protestant",  title: "Protestant" },
  { id: "orthodox",    title: "Orthodox" },
  { id: "other",       title: "Other" },
  { id: "skip",        title: "Skip" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [displayName, setDisplayName] = useState("");
  const [style, setStyle] = useState("gentle");
  const [denom, setDenom] = useState("skip");
  const [memoryConsent, setMemoryConsent] = useState(false);
  const [disclosureAck, setDisclosureAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  // Bounce anyone here without a JWT cookie back to /
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { body } = await getMe();
      if (cancelled) return;
      if (!body || body.scope !== "user") {
        navigate("/", { replace: true });
        return;
      }
      if (body.onboarding_complete) {
        navigate("/poc", { replace: true });
        return;
      }
      setDisplayName(body.display_name || "");
      setAuthReady(true);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const next = () => setStep((s) => Math.min(4, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!disclosureAck || submitting) return;
    setSubmitting(true);
    const { ok } = await postOnboarding({
      display_name: displayName.trim() || "Friend",
      response_style: style,
      denomination: denom,
      memory_consent: memoryConsent,
      disclosure_ack: true,
    });
    setSubmitting(false);
    if (ok) navigate("/poc", { replace: true });
  };

  if (!authReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--background))]">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div
      data-testid="onboarding-page"
      className="relative flex min-h-screen flex-col items-center justify-center bg-[hsl(var(--background))] px-4 py-10"
    >
      <div className="w-full max-w-lg rounded-3xl border border-border/70 bg-[hsl(var(--card))] px-7 py-9 shadow-[0_22px_60px_-20px_rgba(59,74,107,0.18)]">
        {/* progress dots */}
        <div className="flex items-center gap-1.5" data-testid="onboarding-progress">
          {[1, 2, 3, 4].map((n) => (
            <span
              key={n}
              className={
                "h-1 rounded-full transition-all " +
                (n < step ? "w-6 bg-[hsl(var(--accent))]/70"
                  : n === step ? "w-9 bg-foreground"
                  : "w-3 bg-border")
              }
            />
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="mt-8" data-testid="onboarding-step-1">
            <h1
              className="text-3xl font-medium tracking-tight text-foreground"
              style={{ fontFamily: "Georgia, 'Cormorant Garamond', serif" }}
            >
              Welcome.
            </h1>
            <p className="mt-2 text-[14px] text-muted-foreground">How would you like Jesus to address you?</p>
            <input
              data-testid="onboarding-display-name"
              type="text"
              autoFocus
              maxLength={64}
              placeholder="Your first name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-6 h-12 w-full rounded-full border border-border/70 bg-background px-5 text-[15px] outline-none transition focus:border-[hsl(var(--accent))]/60 focus:ring-2 focus:ring-[hsl(var(--accent))]/30"
            />
            <div className="mt-7 flex justify-end">
              <Button
                onClick={next}
                disabled={!displayName.trim()}
                data-testid="onboarding-next"
                className="h-11 rounded-full bg-[hsl(var(--primary))] px-6 text-[hsl(var(--primary-foreground))] disabled:opacity-50"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="mt-8" data-testid="onboarding-step-2">
            <h1
              className="text-3xl font-medium tracking-tight text-foreground"
              style={{ fontFamily: "Georgia, 'Cormorant Garamond', serif" }}
            >
              How should I speak with you?
            </h1>
            <p className="mt-2 text-[14px] text-muted-foreground">You can change this later.</p>
            <div className="mt-6 flex flex-col gap-3" role="radiogroup">
              {STYLES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  role="radio"
                  aria-checked={style === s.id}
                  onClick={() => setStyle(s.id)}
                  data-testid={`onboarding-style-${s.id}`}
                  className={
                    "flex flex-col items-start gap-1 rounded-2xl border px-5 py-4 text-left transition " +
                    (style === s.id
                      ? "border-[hsl(var(--accent))]/70 bg-[hsl(var(--accent))]/10"
                      : "border-border/70 hover:bg-muted/30")
                  }
                >
                  <span className="text-[15px] font-medium text-foreground">{s.title}</span>
                  <span className="text-[12px] text-muted-foreground">{s.body}</span>
                </button>
              ))}
            </div>
            <div className="mt-7 flex justify-between">
              <Button onClick={back} variant="ghost" data-testid="onboarding-back" className="h-11 rounded-full px-5 text-muted-foreground">Back</Button>
              <Button onClick={next} data-testid="onboarding-next" className="h-11 rounded-full bg-[hsl(var(--primary))] px-6 text-[hsl(var(--primary-foreground))]">
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="mt-8" data-testid="onboarding-step-3">
            <h1
              className="text-3xl font-medium tracking-tight text-foreground"
              style={{ fontFamily: "Georgia, 'Cormorant Garamond', serif" }}
            >
              Any tradition you'd like me to be aware of?
            </h1>
            <p className="mt-2 text-[14px] text-muted-foreground">Optional. Helps me frame answers.</p>
            <div className="mt-6 flex flex-wrap gap-2" role="radiogroup">
              {DENOMS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  role="radio"
                  aria-checked={denom === d.id}
                  onClick={() => setDenom(d.id)}
                  data-testid={`onboarding-denom-${d.id}`}
                  className={
                    "rounded-full border px-4 py-2 text-[13px] transition " +
                    (denom === d.id
                      ? "border-[hsl(var(--accent))]/70 bg-[hsl(var(--accent))]/10 text-foreground"
                      : "border-border/70 text-muted-foreground hover:bg-muted/30")
                  }
                >
                  {d.title}
                </button>
              ))}
            </div>
            <div className="mt-7 flex justify-between">
              <Button onClick={back} variant="ghost" data-testid="onboarding-back" className="h-11 rounded-full px-5 text-muted-foreground">Back</Button>
              <Button onClick={next} data-testid="onboarding-next" className="h-11 rounded-full bg-[hsl(var(--primary))] px-6 text-[hsl(var(--primary-foreground))]">
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <div className="mt-8" data-testid="onboarding-step-4">
            <h1
              className="text-3xl font-medium tracking-tight text-foreground"
              style={{ fontFamily: "Georgia, 'Cormorant Garamond', serif" }}
            >
              Before we begin.
            </h1>

            <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-2xl border border-border/70 px-4 py-3 transition hover:bg-muted/20">
              <input
                type="checkbox"
                checked={memoryConsent}
                onChange={(e) => setMemoryConsent(e.target.checked)}
                data-testid="onboarding-memory-consent"
                className="mt-0.5 h-4 w-4 accent-[hsl(var(--accent))]"
              />
              <span className="text-[14px] leading-relaxed text-foreground">
                I'd like Jesus to remember things I share over time.
                <span className="block text-[12px] text-muted-foreground/80">
                  You can review and clear remembered facts later in settings.
                </span>
              </span>
            </label>

            <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-2xl border border-border/70 px-4 py-3 transition hover:bg-muted/20">
              <input
                type="checkbox"
                checked={disclosureAck}
                onChange={(e) => setDisclosureAck(e.target.checked)}
                data-testid="onboarding-disclosure-ack"
                className="mt-0.5 h-4 w-4 accent-[hsl(var(--accent))]"
                required
              />
              <span className="text-[13px] leading-relaxed text-foreground">
                I understand this is an AI companion inspired by the teachings of Jesus,
                not literal communication with Jesus, God, or any religious authority.
                It may be incomplete or wrong. It is not medical, mental-health, legal,
                financial, or emergency advice. If I'm in crisis I'll contact local
                emergency services or a crisis hotline.
              </span>
            </label>

            <div className="mt-7 flex justify-between">
              <Button onClick={back} variant="ghost" data-testid="onboarding-back" className="h-11 rounded-full px-5 text-muted-foreground">Back</Button>
              <Button
                onClick={submit}
                disabled={!disclosureAck || submitting}
                data-testid="onboarding-begin"
                className="h-11 rounded-full bg-[hsl(var(--primary))] px-6 text-[hsl(var(--primary-foreground))] disabled:opacity-60"
              >
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : "Begin"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
